import React from "react";

const SurgeryConsentPage = ({
    pageRefs,
    currentPage,
    logo,
    pageTitles,
    SurgeryFormData,
    surgeonOptionsMap,
    docSignature,
    setSignatures,
    setSurgeryFormData,
    handleInputChange,
    invalidFields,
    isNext,
    isEditing,
    signatures,
    canvasRefs,
    updateSignatureData,
    clearSignature,
    toggleEditMode,
    getCentralAmericaDate,
    getCentralAmericaTime,

    // Components passed in
    Logo,
    Head,
    Paragraph,
    RadioGroup,
    TextInput,
    SignatureField,
}) => {
    const practice = localStorage.getItem("digital-form-dept-practice");

    return (
        <div ref={pageRefs.current[currentPage]} className="max-w-4xl w-full bg-white px-2 mx-5 py-2">
            <div className="px-1 mx-8 py-2">
                <Logo src={logo} align="right" />
                <Head title={pageTitles['Summit Surgery Center'][currentPage]} />
                <Paragraph >Patient Name: {SurgeryFormData.first_name + " " + SurgeryFormData.last_name}</Paragraph>
                <Paragraph>
                    You have been given information about your condition and the recommended surgical, medical or diagnostic procedure(s)
                    to be used. This consent form is designed to provide a written confirmation of such discussions by recording some of the
                    more significant medical information given to you. It is intended to make you better informed so that you may give or
                    withhold your consent to the proposed procedure(s).
                </Paragraph>
                <Paragraph>
                    1. Condition: I have been informed that the following condition(s) exist in my case by doctor:
                </Paragraph>

                {/* Row 9: Anesthesia */}
                <RadioGroup
                    label=""
                    name="surgeon"
                    options={surgeonOptionsMap[practice] || []}
                    selectedValue={SurgeryFormData.surgeon}
                    onChange={(value) => {
                        const selectedSignature = docSignature[value];

                        setSignatures((prev) => ({
                            ...prev,
                            doctor_signature: selectedSignature,
                        }));

                        setSurgeryFormData((prev) => ({
                            ...prev,
                            surgeon: value,
                            doctor_signature: selectedSignature,
                        }));
                    }

                    }
                    disabled={isNext}
                />


                <div>
                    {" "}
                    <TextInput
                        id="diagnosis"
                        label=""
                        value={SurgeryFormData.diagnosis}
                        onChange={handleInputChange}
                        disabled={isNext}
                        invalid={invalidFields.includes("diagnosis") && !SurgeryFormData.diagnosis}
                    />
                </div>
                <Paragraph>
                    2. Proposed Procedure(s): I understand that the procedure(s) for evaluating and treating my condition is/are:
                </Paragraph>
                <div>
                    {" "}
                    <TextInput
                        id="procedure_description"
                        label=""
                        value={SurgeryFormData.procedure_description}
                        onChange={handleInputChange}
                        disabled={isNext}
                        invalid={invalidFields.includes("procedure_description") && !SurgeryFormData.procedure_description}
                    />
                </div>
                <Paragraph>
                    3. Risks/Benefits of Proposed Procedure(s):
                    A. Just as there may be benefits to the procedure(s) proposed, I also understand that medical and surgical procedures
                    involve risks. These risks include allergic reactions, bleeding, blood clots, infections, adverse side effects of drugs,
                    and even loss of bodily function or life, as well as risks of transfusion reactions and the transmission or infectious
                    disease, including Hepatitis and Acquired Immune Deficiency Syndrome from the administration of blood and/or
                    blood components.
                    B. I also realize that there are particular risks associated with the procedure(s) proposed for me and that these risks
                    include, but are not limited to: failure to accomplish the intent of the surgery, retinal detachment that may require
                    additional surgery or may be inoperable, hemorrhage, infection/inflammation, elevated eye pressure (glaucoma),
                    corneal clouding and scarring, cataract which might require eventual or immediate removal of lens, double vision,
                    eyelid droops, loss of circulation to vital tissues in the eye resulting in decrease or loss of vision, permanent
                    blindness or diminished visual acuity or field, loss of eye, pain.
                </Paragraph>
                <Paragraph>
                    4. Complications: Unforeseen Conditions; Results:
                    I am aware that in the practice of medicine other unexpected risks or complications not discussed may occur. I also
                    understand that during the course of the proposed procedure(s) unforeseen conditions may be revealed requiring the
                    performance of additional procedures, and I authorize such procedures to be performed. acknowledge that no
                    guarantees or promises have been made to me concerning the results of any procedure or treatment.
                </Paragraph>
                <div>
                    5. Acknowledgements: The available alternatives, some of which include:
                    {" "}
                    <TextInput
                        id="alternatives"
                        label=""
                        value={SurgeryFormData.alternatives}
                        onChange={handleInputChange}
                        disabled={isNext}
                        invalid={invalidFields.includes("alternatives") && !SurgeryFormData.alternatives}
                    />
                    <Paragraph>

                        The potential benefits and risks ofthe proposed procedure(s), and the likely result without such treatment have been
                        explained to me. I understand what has been discussed with me as well as the contents of this consent form and have been
                        given the opportunity to ask questions and have received satisfactory answers.
                    </ Paragraph>
                </div>
                <Paragraph>
                    6. Consent to Procedure(s) and Treatment: Having read this form and talked with the physicians,my signature below
                    acknowledges that I voluntarily give my authorization and consent to the performance of the procedure(s) described
                    above (including the administration of blood and disposal of tissue) by my physician and/or his/her associates assisted
                    by hospital personnel and other trained persons as well as the presence of observers.
                </Paragraph>

                <div className="mb-4 flex flex-col sm:flex-row sm:items-start gap-6">
                    {/* Signature */}
                    <SignatureField
                        fieldName="patient_signature"
                        label="Patient Signature"
                        formData={SurgeryFormData}
                        isEditing={isEditing}
                        isDisabled={isNext}
                        signatures={signatures}
                        canvasRefs={canvasRefs}
                        invalidFields={invalidFields}
                        updateSignatureData={updateSignatureData}
                        clearSignature={clearSignature}
                        toggleEditMode={toggleEditMode}
                        isNext={isNext}
                    />

                    {/* Date & Time */}
                    <div className="mb-4 flex flex-col sm:flex-row flex-wrap gap-4 items-center">
                        {SurgeryFormData['patient_signature'] && (
                            <>
                                <p className="font-roboto text-base text-black whitespace-nowrap mx-1">
                                    {SurgeryFormData.patientSignedDate}
                                </p>
                                <p className="font-roboto text-base text-black whitespace-nowrap">
                                    {SurgeryFormData.patientSignedTime}
                                </p>
                            </>
                        )}
                    </div>
                </div>


                <div className="mb-4 flex flex-col sm:flex-row sm:items-start gap-6">
                    {/* Signature */}
                    <div className="flex-shrink-0">
                        <label className="block text-base text-black mb-3">
                            Physician/Surgeon
                        </label>
                        {SurgeryFormData['doctor_signature'] ? (
                            <img
                                src={SurgeryFormData['doctor_signature']}
                                alt="Signature"
                                loading="eager"
                                style={{
                                    width: '300px',
                                    height: '100px',
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    borderRadius: '0.375rem',
                                    border: '1px solid #D1D5DB',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        ) : null}
                    </div>

                    {/* Date & Time */}
                    <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-center">
                        <p className="font-roboto text-base text-black whitespace-nowrap mx-1">
                            {SurgeryFormData.signedDate}
                        </p>
                        <p className="font-roboto text-base text-black whitespace-nowrap">
                            {SurgeryFormData.signedTime}
                        </p>
                        {!isNext && (
                            <button
                                type="button"
                                onClick={() =>
                                    setSurgeryFormData({
                                        ...SurgeryFormData,
                                        signedDate: getCentralAmericaDate(),
                                        signedTime: getCentralAmericaTime(),
                                    })
                                }
                                disabled={isNext}
                                className="p-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white focus:outline-none"
                                title="Set Central America Date & Time"
                            >
                                <i className="fas fa-calendar-days text-white text-lg"></i>
                            </button>
                        )}
                    </div>
                </div>

                <div className="mb-4 flex flex-col sm:flex-row sm:items-start gap-6">
                    {/* Signature */}
{/* 
                    <SignatureField
                        fieldName="witness_signature"
                        label="Witness Signature"
                        formData={SurgeryFormData}
                        isEditing={isEditing}
                        isDisabled={isNext}
                        signatures={signatures}
                        canvasRefs={canvasRefs}
                        invalidFields={invalidFields}
                        updateSignatureData={updateSignatureData}
                        clearSignature={clearSignature}
                        toggleEditMode={toggleEditMode}
                        isNext={isNext}
                    /> */}

                    {/* Date & Time
                    <div className="mb-4 flex flex-col sm:flex-row flex-wrap gap-4 items-center">
                        {SurgeryFormData['witness_signature'] && (
                            <>
                                <p className="font-roboto text-base text-black whitespace-nowrap mx-1">
                                    {SurgeryFormData.witnessSignedDate}
                                </p>
                                <p className="font-roboto text-base text-black whitespace-nowrap">
                                    {SurgeryFormData.witnessSignedTime}
                                </p>
                            </>
                        )}
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default SurgeryConsentPage;